import { memo, useCallback, useMemo, useRef } from 'react';
import CustomInput from 'src/componentes/CustomInput';
import { useIntl } from 'react-intl';
import {
  ArrowDown,
  ArrowUp,
  Copy,
  GripHorizontal,
  Image as ImageIcon,
  Trash2,
} from 'lucide-react';

import Button from 'src/componentes/button';
import ContentManager from 'src/componentes/content-manager';
import Confirm from 'src/componentes/message-box/confirm';
import SelectImage from 'src/componentes/arquivo-upload/select-image';
import ConfigApp from 'src/services/configApp';
import css from 'src/utils/css';
import FormularioGlobalBufferedInput from './FormularioGlobalBufferedInput';
import FormularioGlobalQuestionOptions from './FormularioGlobalQuestionOptions';
import FormularioGlobalQuestionMatrix from './FormularioGlobalQuestionMatrix';
import FormularioGlobalResizableImage, {
  FORMULARIO_GLOBAL_IMAGE_SIZE,
  normalizeStoredImageSize,
} from './FormularioGlobalResizableImage';
import {
  createDefaultQuestionOptions,
  createFormularioGlobalQuestion,
  areQuestionMatrixSettingsEqual,
  FORMULARIO_GLOBAL_QUESTION_TYPES,
  getQuestionTypeOptions,
  normalizeQuestionCommentSettings,
  normalizeQuestionOptions,
  QUESTION_TYPES_WITH_OPTIONS,
} from './FormularioGlobalQuestionTypes';

import {
  canConfigureQuestionBranching,
  isQuestionBranchingEnabled,
} from './formularioGlobalBranching';

const QUESTION_TITLE_MAX_LENGTH = 1000;

const classes = css`
  .question {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
    padding: 18px 22px;
  }

  .drag {
    display: flex;
    justify-content: center;
    margin-bottom: 8px;
  }

  .dragHandle {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: #94a3b8;
    cursor: move !important;
    display: inline-flex;
    height: 30px;
    justify-content: center;
    padding: 0;
    width: 30px;
  }

  .dragHandle:active {
    cursor: move !important;
  }

  .dragHandleDragging {
    cursor: move !important;
  }

  .dragHandle:hover {
    background: #f1f5f9;
    color: #0f7180;
  }

  .dragHandle:disabled {
    color: #cbd5e1;
    cursor: not-allowed;
  }

  .body {
    display: grid;
    gap: 16px;
    grid-template-columns: minmax(0, 1fr) minmax(220px, 260px);
  }

  .index {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 6px;
  }

  .titleInputGroup {
    margin-bottom: 0;
  }

  .titleInputControl {
    border: 1px solid #dde6e9;
    border-radius: 6px;
    box-sizing: border-box;
    color: #0f172a;
    font-size: 14px;
    font-weight: 800;
    height: 40px !important;
    line-height: 20px;
    max-height: 40px;
    min-height: 40px !important;
    padding: 6px 10px !important;
  }

  .typeLabel {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 6px;
  }

  .typeSelect {
    background: #ffffff;
    border: 1px solid #dde6e9;
    border-radius: 6px;
    box-sizing: border-box;
    color: #475569;
    height: 40px;
    line-height: 20px;
    min-height: 40px;
    padding: 6px 10px;
    width: 100%;
  }

  .actions {
    align-items: center;
    border-top: 1px solid #e2e8f0;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: space-between;
    margin-top: 16px;
    padding-top: 12px;
  }

  .leftActions,
  .rightActions {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .iconButton {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: #475569;
    display: inline-flex;
    height: 30px;
    justify-content: center;
    padding: 0;
    width: 30px;

    &:not(:disabled):hover {
      background: #f1f5f9;
      color: #0f7180;
    }

    &:disabled {
      color: #cbd5e1;
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  .required {
    align-items: center;
    border-left: 1px solid #e2e8f0;
    display: flex;
    gap: 10px;
    padding-left: 14px;
  }

  .requiredLabel {
    color: #475569;
    font-size: 12px;
    margin-bottom: 0;
  }

  .imageSettings {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(2, minmax(120px, 180px));
    margin-top: 12px;
  }

  @media (max-width: 768px) {
    .body {
      grid-template-columns: 1fr;
    }

    .actions {
      align-items: flex-start;
      flex-direction: column;
    }
  }
`;

const getImageGuid = (imagem) => {
  if (!imagem) return null;
  if (typeof imagem === 'string') return imagem;

  return imagem.guid || imagem.imagemGuid || null;
};

const applyCommentSettingsToOptions = (
  question,
  permitirComentario,
  comentarioObrigatorio
) => ({
  ...question,
  permitirComentario,
  comentarioObrigatorio: permitirComentario && comentarioObrigatorio,
  opcoes: (question.opcoes || []).map((opcao) => ({
    ...opcao,
    permitirComentario,
    comentarioObrigatorio: permitirComentario && comentarioObrigatorio,
  })),
});

const areQuestionOptionsEqual = (previousOptions = [], nextOptions = []) =>
  previousOptions.length === nextOptions.length &&
  previousOptions.every((previousOption, index) => {
    const nextOption = nextOptions[index];

    return (
      previousOption.id === nextOption?.id &&
      previousOption.texto === nextOption?.texto &&
      previousOption.titulo === nextOption?.titulo &&
      previousOption.valor === nextOption?.valor &&
      previousOption.destinoRamificacao === nextOption?.destinoRamificacao &&
      previousOption.destinoRamificacaoGuid ===
        nextOption?.destinoRamificacaoGuid &&
      previousOption.permitirComentario === nextOption?.permitirComentario &&
      previousOption.comentarioObrigatorio ===
        nextOption?.comentarioObrigatorio &&
      previousOption.ordem === nextOption?.ordem
    );
  });

const areQuestionsEqual = (previousQuestion = {}, nextQuestion = {}) =>
  previousQuestion.id === nextQuestion.id &&
  previousQuestion.guid === nextQuestion.guid &&
  previousQuestion.titulo === nextQuestion.titulo &&
  previousQuestion.tipo === nextQuestion.tipo &&
  previousQuestion.obrigatoria === nextQuestion.obrigatoria &&
  previousQuestion.aplicarRamificacao === nextQuestion.aplicarRamificacao &&
  previousQuestion.permitirComentario === nextQuestion.permitirComentario &&
  previousQuestion.comentarioObrigatorio ===
    nextQuestion.comentarioObrigatorio &&
  previousQuestion.arquivoSistemaId === nextQuestion.arquivoSistemaId &&
  getImageGuid(previousQuestion.imagem) === getImageGuid(nextQuestion.imagem) &&
  previousQuestion.imagemLargura === nextQuestion.imagemLargura &&
  previousQuestion.imagemAltura === nextQuestion.imagemAltura &&
  previousQuestion.ordem === nextQuestion.ordem &&
  areQuestionMatrixSettingsEqual(previousQuestion, nextQuestion) &&
  areQuestionOptionsEqual(previousQuestion.opcoes, nextQuestion.opcoes);

const FormularioGlobalQuestionCard = ({
  branchingTargets,
  dragHandleProps,
  disabled,
  isFirst,
  isDragging,
  isLast,
  numero,
  exibirConfiguracaoComentario = true,
  exibirConfiguracaoComentarioOpcao = true,
  onChange,
  onClone,
  onMoveDown,
  onMoveUp,
  onRemove,
  question,
  randomizarPerguntas,
  scopeId,
  validationErrors,
}) => {
  const intl = useIntl();
  const questionTypes = useMemo(() => getQuestionTypeOptions(intl), [intl]);
  const imageGuid = getImageGuid(question.imagem);
  const imageUrl = useMemo(
    () =>
      imageGuid
        ? `${ConfigApp.ApiUrl}/ArquivoSistema/DownloadImagem?guid=${imageGuid}`
        : null,
    [imageGuid]
  );
  const imageSize = useMemo(
    () =>
      normalizeStoredImageSize(question.imagemLargura, question.imagemAltura),
    [question.imagemAltura, question.imagemLargura]
  );
  const questionRef = useRef(question);
  questionRef.current = question;

  const handleChange = useCallback(
    (field, value) => {
      onChange({ ...questionRef.current, [field]: value });
    },
    [onChange]
  );

  const handleChangeType = useCallback(
    (event) => {
      const tipo = event.target.value;
      const { linhas, colunas, permitirMultiplaSelecaoPorLinha } =
        createFormularioGlobalQuestion(tipo, questionRef.current.ordem, intl);
      const nextQuestion = {
        ...questionRef.current,
        tipo,
        aplicarRamificacao: false,
        linhas,
        colunas,
        permitirMultiplaSelecaoPorLinha,
        opcoes: createDefaultQuestionOptions(tipo, intl),
      };

      onChange(
        applyCommentSettingsToOptions(
          nextQuestion,
          !!nextQuestion.permitirComentario,
          !!nextQuestion.comentarioObrigatorio
        )
      );
    },
    [intl, onChange]
  );

  const handleChangeOptions = useCallback(
    (opcoes) => {
      onChange(
        normalizeQuestionCommentSettings({
          ...questionRef.current,
          opcoes: normalizeQuestionOptions(opcoes),
        })
      );
    },
    [onChange]
  );

  const handleChangePermitirComentario = useCallback(
    (permitirComentario) => {
      onChange(
        normalizeQuestionCommentSettings({
          ...applyCommentSettingsToOptions(
            questionRef.current,
            permitirComentario,
            permitirComentario && !!questionRef.current.comentarioObrigatorio
          ),
        })
      );
    },
    [onChange]
  );

  const handleChangeComentarioObrigatorio = useCallback(
    (comentarioObrigatorio) => {
      onChange(
        normalizeQuestionCommentSettings({
          ...applyCommentSettingsToOptions(
            questionRef.current,
            !!questionRef.current.permitirComentario,
            comentarioObrigatorio
          ),
        })
      );
    },
    [onChange]
  );

  const handleResizeImage = useCallback(
    ({ width, height }) => {
      onChange({
        ...questionRef.current,
        imagemLargura: width,
        imagemAltura: height,
      });
    },
    [onChange]
  );

  const handleUploadImage = useCallback(() => {
    if (disabled) return;

    ContentManager.addContent(
      <SelectImage
        title={intl.formatMessage({
          id: 'labelAdicionarImagemPergunta',
        })}
        model={{
          requestChange: (arquivo) => handleChange('imagem', arquivo),
        }}
      />
    );
  }, [disabled, handleChange, intl]);

  const handleRemove = useCallback(() => {
    ContentManager.addContent(
      <Confirm
        message={intl.formatMessage({
          id: 'labelDesejaMesmoExcluirEstaPergunta',
        })}
        handleConfirm={onRemove}
      />
    );
  }, [intl, onRemove]);

  const hasOptions = QUESTION_TYPES_WITH_OPTIONS.includes(question.tipo);
  const aplicarRamificacao = isQuestionBranchingEnabled(question);
  const branchingId = `form-question-branching-${scopeId}-${question.ordem}`;
  const requiredId = `form-question-required-${scopeId}-${question.ordem}`;
  const commentId = `form-question-comment-${scopeId}-${question.ordem}`;
  const commentRequiredId = `form-question-comment-required-${scopeId}-${question.ordem}`;

  return (
    <div className={classes.question}>
      <div className={classes.drag}>
        <button
          disabled={disabled}
          type="button"
          {...dragHandleProps}
          className={`${classes.dragHandle} ${
            isDragging ? classes.dragHandleDragging : ''
          }`}
        >
          <GripHorizontal size={20} />
        </button>
      </div>
      <div className={classes.body}>
        <div>
          <div className={classes.index}>
            {intl.formatMessage(
              { id: 'labelPerguntaNumero' },
              { numero: numero ?? question.ordem }
            )}
          </div>
          <div className={classes.titleInput}>
            <FormularioGlobalBufferedInput
              className={classes.titleInputGroup}
              commitOnChange
              disabled={disabled}
              inputClassName={classes.titleInputControl}
              maxLength={QUESTION_TITLE_MAX_LENGTH}
              onCommit={(value) => handleChange('titulo', value)}
              placeholder={intl.formatMessage({
                id: 'labelInsiraUmaPergunta',
              })}
              value={question.titulo}
              validationMessage={
                validationErrors?.titulo
                  ? intl.formatMessage({ id: validationErrors.titulo })
                  : null
              }
            />
          </div>
          {hasOptions && (
            <FormularioGlobalQuestionOptions
              aplicarRamificacao={aplicarRamificacao}
              branchingTargets={branchingTargets}
              questionKey={question._formularioGlobalClientId}
              randomizarPerguntas={randomizarPerguntas}
              disabled={disabled}
              idPrefix={`question-option-${scopeId}-${question.ordem}`}
              onChange={handleChangeOptions}
              opcoes={question.opcoes}
              exibirConfiguracaoComentarioOpcao={
                exibirConfiguracaoComentarioOpcao
              }
              comentarioObrigatorio={!!question.comentarioObrigatorio}
              permitirComentario={!!question.permitirComentario}
              tipo={question.tipo}
            />
          )}
          {aplicarRamificacao &&
            !randomizarPerguntas &&
            validationErrors?.ramificacao &&
            !validationErrors.ramificacao.toast && (
              <p
                className="tw:mb-0 tw:mt-2 tw:text-sm tw:text-red-600"
                role="alert"
              >
                {intl.formatMessage(
                  { id: validationErrors.ramificacao.id },
                  validationErrors.ramificacao.values
                )}
              </p>
            )}
          {question.tipo ===
            FORMULARIO_GLOBAL_QUESTION_TYPES.MATRIZ_SELECAO && (
            <FormularioGlobalQuestionMatrix
              disabled={disabled}
              idPrefix={`question-matrix-${scopeId}-${question.ordem}`}
              onChange={onChange}
              question={question}
              validationErrors={validationErrors}
            />
          )}
          {imageUrl && (
            <FormularioGlobalResizableImage
              alt={
                question.titulo ||
                intl.formatMessage({ id: 'labelImagemPergunta' })
              }
              disabled={disabled}
              height={imageSize.height}
              onResize={handleResizeImage}
              src={imageUrl}
              width={imageSize.width}
            />
          )}
          {imageUrl && (
            <div className={classes.imageSettings}>
              <FormularioGlobalBufferedInput
                disabled={disabled}
                label={intl.formatMessage({
                  id: 'labelLarguraImagem',
                })}
                max={FORMULARIO_GLOBAL_IMAGE_SIZE.MAX_WIDTH}
                min={FORMULARIO_GLOBAL_IMAGE_SIZE.MIN_WIDTH}
                onCommit={(value) =>
                  handleChange(
                    'imagemLargura',
                    normalizeStoredImageSize(value, null).width
                  )
                }
                type="number"
                value={imageSize.width || ''}
              />
              <FormularioGlobalBufferedInput
                disabled={disabled}
                label={intl.formatMessage({
                  id: 'labelAlturaImagem',
                })}
                max={FORMULARIO_GLOBAL_IMAGE_SIZE.MAX_HEIGHT}
                min={FORMULARIO_GLOBAL_IMAGE_SIZE.MIN_HEIGHT}
                onCommit={(value) =>
                  handleChange(
                    'imagemAltura',
                    normalizeStoredImageSize(null, value).height
                  )
                }
                type="number"
                value={imageSize.height || ''}
              />
            </div>
          )}
          {imageUrl && !disabled && (
            <Button
              color="secondary"
              leftIcon={Trash2}
              onClick={() => handleChange('imagem', null)}
              size="sm"
            >
              {intl.formatMessage({
                id: 'labelRemoverImagem',
              })}
            </Button>
          )}
        </div>
        <div>
          <div className={classes.typeLabel}>
            {intl.formatMessage({
              id: 'labelTipoPergunta',
            })}
          </div>
          <select
            className={classes.typeSelect}
            disabled={disabled}
            onChange={handleChangeType}
            value={question.tipo}
          >
            {questionTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className={classes.actions}>
        <div className={classes.leftActions}>
          <button
            className={classes.iconButton}
            disabled={disabled || isFirst}
            onClick={onMoveUp}
            title={intl.formatMessage({
              id: 'labelMoverCima',
            })}
            type="button"
          >
            <ArrowUp size={18} />
          </button>
          <button
            className={classes.iconButton}
            disabled={disabled || isLast}
            onClick={onMoveDown}
            title={intl.formatMessage({
              id: 'labelMoverBaixo',
            })}
            type="button"
          >
            <ArrowDown size={18} />
          </button>
          <button
            className={classes.iconButton}
            disabled={disabled}
            onClick={onClone}
            title={intl.formatMessage({
              id: 'labelClonar',
            })}
            type="button"
          >
            <Copy size={18} />
          </button>
          <button
            className={classes.iconButton}
            disabled={disabled}
            onClick={handleRemove}
            title={intl.formatMessage({
              id: 'excluir',
            })}
            type="button"
          >
            <Trash2 size={18} />
          </button>
          <button
            className={classes.iconButton}
            disabled={disabled}
            onClick={handleUploadImage}
            title={intl.formatMessage({
              id: 'labelAdicionarImagemPergunta',
            })}
            type="button"
          >
            <ImageIcon size={18} />
          </button>
        </div>
        <div className={classes.rightActions}>
          {canConfigureQuestionBranching(question.tipo) && (
            <div className={classes.required}>
              <label className={classes.requiredLabel} htmlFor={branchingId}>
                {intl.formatMessage({ id: 'labelAplicarRamificacao' })}
              </label>
              <CustomInput
                checked={aplicarRamificacao}
                disabled={disabled}
                id={branchingId}
                onChange={(event) =>
                  handleChange('aplicarRamificacao', event.target.checked)
                }
                type="switch"
              />
            </div>
          )}
          {exibirConfiguracaoComentario && (
            <div className={classes.required}>
              <label className={classes.requiredLabel} htmlFor={commentId}>
                {intl.formatMessage({
                  id: 'labelPermitirComentario',
                })}
              </label>
              <CustomInput
                checked={!!question.permitirComentario}
                disabled={disabled}
                id={commentId}
                onChange={(event) =>
                  handleChangePermitirComentario(event.target.checked)
                }
                type="switch"
              />
            </div>
          )}
          {exibirConfiguracaoComentario && (
            <div className={classes.required}>
              <label
                className={classes.requiredLabel}
                htmlFor={commentRequiredId}
              >
                {intl.formatMessage({
                  id: 'labelComentarioObrigatorio',
                })}
              </label>
              <CustomInput
                checked={
                  !!question.permitirComentario &&
                  !!question.comentarioObrigatorio
                }
                disabled={disabled || !question.permitirComentario}
                id={commentRequiredId}
                onChange={(event) =>
                  handleChangeComentarioObrigatorio(event.target.checked)
                }
                type="switch"
              />
            </div>
          )}
          <div className={classes.required}>
            <label className={classes.requiredLabel} htmlFor={requiredId}>
              {intl.formatMessage({
                id: 'labelSimplesPerguntaObrigatoria',
              })}
            </label>
            <CustomInput
              checked={!!question.obrigatoria}
              disabled={disabled}
              id={requiredId}
              onChange={(event) =>
                handleChange('obrigatoria', event.target.checked)
              }
              type="switch"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(
  FormularioGlobalQuestionCard,
  (previousProps, nextProps) =>
    previousProps.branchingTargets === nextProps.branchingTargets &&
    previousProps.randomizarPerguntas === nextProps.randomizarPerguntas &&
    previousProps.disabled === nextProps.disabled &&
    previousProps.isFirst === nextProps.isFirst &&
    previousProps.isDragging === nextProps.isDragging &&
    previousProps.isLast === nextProps.isLast &&
    previousProps.numero === nextProps.numero &&
    previousProps.exibirConfiguracaoComentario ===
      nextProps.exibirConfiguracaoComentario &&
    previousProps.exibirConfiguracaoComentarioOpcao ===
      nextProps.exibirConfiguracaoComentarioOpcao &&
    previousProps.scopeId === nextProps.scopeId &&
    previousProps.validationErrors === nextProps.validationErrors &&
    areQuestionsEqual(previousProps.question, nextProps.question)
);
